import { FormEvent, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { Form, FormControl } from 'react-bootstrap';
import { useSuperState } from 'hooks';

export type PromptsOptions = {
    initialValue?: string;
    placeholder?: string;
    required?: boolean;
};

/**
 * The class to represent prompt options
 */
export class Prompts {

    initialValue?: string;

    placeholder?: string;

    required?: boolean;

    constructor(options: PromptsOptions = {}) {

        const { initialValue, placeholder, required } = options;
        this.initialValue = initialValue;
        this.placeholder = placeholder;
        this.required = required;

    }

}

/**
 * The class to represent text prompt options
 */
export class TextPrompt extends Prompts {}

/**
 * The class to represent password prompt options
 */
export class PasswordPrompt extends Prompts {}

/**
 * The class to represent number prompt options
 */
export class NumberPrompt extends Prompts {}

export type PromptInputHandle = {
    getValue: () => string;
};

type PromptInputProps = {
    onSubmit?: () => void;
    prompt: Prompts;
};

export const PromptInput = forwardRef<PromptInputHandle, PromptInputProps>((props, forwardedRef) => {

    const { onSubmit, prompt } = props;

    const [state, setState] = useSuperState({
        value: prompt.initialValue || ''
    });

    const formRef = useRef<HTMLFormElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {

        inputRef.current?.select();

    }, []);

    // useSuperState mutates the state object in-place, so state.value is always current
    // inside this closure even with an empty dep array — no stale-closure risk here.
    useImperativeHandle(forwardedRef, () => ({
        getValue: () => state.value
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    const checkValidity = () => {

        const formElement = formRef.current;

        if (formElement && !formElement.checkValidity()) {

            formElement.reportValidity();
            return false;

        }

        return true;

    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {

        e.preventDefault();
        if (!checkValidity()) return;
        if (typeof onSubmit === 'function') onSubmit();

    };

    // Determine the input type based on the instantiated prompt class.
    // Uses instanceof instead of constructor.name to be safe under minification.
    let type: string | undefined;

    if (prompt instanceof PasswordPrompt) {

        type = 'password';

    } else if (prompt instanceof TextPrompt) {

        type = 'text';

    } else if (prompt instanceof NumberPrompt) {

        type = 'number';

    }

    return (
        <Form onSubmit={handleSubmit} ref={formRef}>
            <FormControl
                autoFocus
                onChange={e => setState({ value: e.target.value })}
                placeholder={prompt.placeholder}
                ref={inputRef}
                required={prompt?.required}
                type={type}
                value={state.value}
            />
        </Form>
    );

});

PromptInput.displayName = 'PromptInput';
